import React, { useEffect, useRef } from 'react';
import { cn } from '../../lib/utils';
import { useViewport } from '../../contexts/ViewportContext';

export interface InputButton {
  id: string;
  icon: React.ReactNode;
  onClick: () => void;
  isActive?: boolean;
  title: string;
  disabled?: boolean;
  color?: 'blue' | 'red' | 'purple' | 'green';
  showWhenEmpty?: boolean; // If true, shows on mobile when input is empty
  showWhenTyping?: boolean; // If true, shows on mobile when user is typing
  hideWhenRecording?: boolean; // If true, hides when recording is active
  hideWhenTyping?: boolean; // If true, hides when user is typing (any device)
  isSubmitButton?: boolean; // Special styling for submit button
}

interface TopicInputProps {
  input: string;
  setInput: (value: string) => void;
  placeholder?: string;
  isRecording?: boolean;
  isTyping?: boolean;
  onSubmit: (e: React.FormEvent) => void;
  buttons: InputButton[];
  extraInfo?: React.ReactNode;
  textareaClassName?: string;
  textareaRows?: number;
  isTextarea?: boolean; // If true, uses textarea, otherwise uses input
}

export function TopicInput({
  input,
  setInput,
  placeholder = 'Type here...',
  isRecording = false,
  isTyping = false,
  onSubmit,
  buttons,
  extraInfo,
  textareaClassName = '',
  textareaRows = 1,
  isTextarea = true,
}: TopicInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { isMobile } = useViewport();

  // Auto-adjust height for textarea
  useEffect(() => {
    if (isTextarea && textareaRef.current) {
      const textarea = textareaRef.current;
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = '44px';
      // Set height to scrollHeight or minimum height (44px), whichever is larger
      textarea.style.height = `${Math.max(44, textarea.scrollHeight)}px`;
      // Ensure consistent line height
      textarea.style.lineHeight = '1.5';
    }
  }, [input, isTextarea]);

  const getButtonClasses = (button: InputButton) => {
    const baseClasses =
      'flex-none w-11 h-11 rounded-lg flex items-center justify-center transition-colors';

    if (button.disabled) {
      return cn(baseClasses, 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-50');
    }

    if (button.isSubmitButton) {
      return cn(
        baseClasses,
        'disabled:opacity-50 disabled:cursor-not-allowed',
        button.color === 'purple'
          ? 'bg-purple-500 text-white hover:bg-purple-600'
          : 'bg-blue-500 text-white hover:bg-blue-600'
      );
    }

    if (button.isActive) {
      return cn(baseClasses, 'bg-red-500 text-white hover:bg-red-600');
    }

    // Regular button styling by color
    switch (button.color) {
      case 'purple':
        return cn(
          baseClasses,
          'bg-purple-100 text-purple-600 hover:bg-purple-200 hover:text-purple-700'
        );
      case 'red':
        return cn(baseClasses, 'bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-700');
      case 'green':
        return cn(
          baseClasses,
          'bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-700'
        );
      case 'blue':
      default:
        return cn(baseClasses, 'bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-700');
    }
  };

  // Determine which buttons to show based on current state
  const visibleButtons = buttons.filter(button => {
    // Hide buttons that should be hidden when recording
    if (button.hideWhenRecording && isRecording) return false;

    // Hide buttons that should be hidden when typing (on all devices)
    if (input.trim() && button.hideWhenTyping) return false;

    // On mobile, show buttons based on input state and button preferences
    if (isMobile && input.trim() && !button.showWhenTyping && !button.isSubmitButton) return false;

    // Hide submit buttons when input is empty (on all devices, not just mobile)
    if (!input.trim() && !button.showWhenEmpty && button.isSubmitButton) return false;

    // Always show recording button
    if (isRecording && button.isActive) return true;

    return true;
  });

  return (
    <form onSubmit={onSubmit} className="relative">
      {extraInfo && <div className="mb-2">{extraInfo}</div>}

      <div className="flex items-center gap-2 md:gap-3 lg:gap-4">
        <div className="relative flex-1">
          {isTextarea ? (
            <textarea
              ref={textareaRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={isTyping ? 'Typing...' : placeholder}
              className={cn(
                'w-full pr-3 py-[10px] pl-3 resize-none rounded-lg',
                'outline-none placeholder-gray-400',
                'min-h-[44px] max-h-82',
                'bg-white leading-[1.2]',
                'flex items-center',
                isMobile && 'text-sm',
                isTyping && 'opacity-50',
                textareaClassName
              )}
              rows={textareaRows}
              disabled={isTyping}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSubmit(e);
                }
              }}
            />
          ) : (
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              placeholder={isTyping ? 'Typing...' : placeholder}
              className={cn(
                'w-full pr-3 py-3 pl-3 rounded-lg',
                'outline-none placeholder-gray-400',
                'h-[44px]',
                'bg-white',
                isMobile && 'text-sm',
                isTyping && 'opacity-50',
                textareaClassName
              )}
              disabled={isTyping}
            />
          )}
        </div>

        <div className="flex items-center gap-2 md:gap-3 lg:gap-4">
          {visibleButtons.map(button => (
            <button
              key={button.id}
              type={button.isSubmitButton ? 'submit' : 'button'}
              onClick={button.isSubmitButton ? undefined : button.onClick}
              disabled={button.disabled}
              className={getButtonClasses(button)}
              title={button.title}
            >
              {button.icon}
            </button>
          ))}
        </div>
      </div>
    </form>
  );
}
